import {
  useCapacityDashboardMutation,
  useCompany,
  useGetCompanyAttributeOptions,
} from '@dispatch/shared/hooks';
import {
  CapacityDashboard_Request,
  CapacityDashboard_RequestSchema,
  CapacityDashboardLocation_RequestSchema,
  MultiSelectOption,
} from '@dispatch/shared/types';
import {
  CapacityDashboard,
  CapacityDashboardFilters,
  CapacityFormData,
} from '@dispatch/web/features/capacity-dashboard';
import { format, isDate, isValid } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import React, { useEffect } from 'react';
import { z } from 'zod';

export const CapacityDashboardPage = () => {
  const { company, perilOptions, perils, teams } = useCompany();
  const companyAttributes = useGetCompanyAttributeOptions(
    company?.attributeDefinition || []
  );
  const companyTeams: MultiSelectOption[] | undefined = React.useMemo(() => {
    return teams?.map((team) => ({
      title: team.name,
      value: team.integrationGUID,
    }));
  }, [company]);

  const offices: MultiSelectOption[] | undefined = React.useMemo(() => {
    return company?.office?.map((office) => ({
      title: office.name,
      value: office.integrationGUID,
    }));
  }, [company]);

  const companyTeamsLookup = React.useMemo(() => {
    if (companyTeams === undefined) return;
    if (companyTeams === null) return;
    if (companyTeams.length === 0) return;

    return companyTeams.reduce(
      (acc: { [key: string]: string }, team: MultiSelectOption) => {
        acc[team.value] = team.title;
        return acc;
      },
      {}
    );
  }, [companyTeams]);

  const assignmentTypes = React.useMemo(
    () =>
      companyAttributes.companyAttributes?.filter(
        (x) => x.value === 'Assignment Type'
      )?.[0]?.attributeDefinitionValues,
    [companyAttributes]
  );
  const [filters, setFilters] =
    React.useState<CapacityDashboard_Request | null>(null);
  const { mutate, data, status } = useCapacityDashboardMutation();
  const [open, setOpen] = React.useState(false);

  const buildFilters = (formData: CapacityFormData) => {
    const {
      locationType,
      unassignedTasks,
      capacityType,
      COLocationType,
      assignedResourceIds,
      perils,
      roles,
      teams,
      assignmentTypes,
      dateRange,
      durationInMinutes,
      averageDriveTime,
      fieldResourcesCapacityPerDay,
      virtualResourcesCapacityPerDay,
      claimOwnerResourcesCapacityPerDay,
      offices,
      attributes,
    } = formData;
    const formatDate = (date: Date | undefined): string => {
      if (!isNil(date) && isDate(date) && isValid(date)) {
        return format(date, 'yyyy-MM-dd');
      }
      throw new Error('Invalid date provided');
    };
    let locations: z.infer<typeof CapacityDashboardLocation_RequestSchema>[] =
      [];

    if (capacityType === 'ClaimOwner') {
      locations = unassignedTasks.map((state) => {
        return {
          type: 'state',
          state: state.location,
          unassignedTasks: state.count > 0 ? state.count : 0,
        };
      });
    } else {
      if (locationType === 'state') {
        locations = unassignedTasks.map((state) => {
          return {
            type: 'state',
            state: state.location,
            unassignedTasks: state.count > 0 ? state.count : 0,
          };
        });
      } else if (locationType === 'county') {
        locations = unassignedTasks.flatMap((county) => {
          const [state, countyName] = county.location.split(' - ');
          if (state && countyName) {
            return {
              type: 'county',
              county: countyName,
              state,
              unassignedTasks: county.count > 0 ? county.count : 0,
            };
          }
          return [];
        });
      } else if (locationType === 'zip') {
        locations = unassignedTasks.map((zip) => ({
          type: 'zip',
          zip: zip.location,
          unassignedTasks: zip.count > 0 ? zip.count : 0,
        }));
      }
    }

    interface AttributeSubmitDTO {
      attributeDefinitionId: string;
      valueIds: string[];
    }

    const transformedAttributes: AttributeSubmitDTO[] =
      attributes?.map((attr) => ({
        attributeDefinitionId: attr.attributeDefinitionIntegrationGUID,
        valueIds: (attr.selectedAttributeValuesVarchars || [])
          .map((v) => v.attributeDefinitionValueIntegrationGUID)
          .filter((id): id is string => !!id),
      })) || [];

    const startDate = dateRange[0] ? formatDate(dateRange[0]) : '';
    const endDate = dateRange[1] ? formatDate(dateRange[1]) : '';
    const teamsValue = !isEmpty(teams) ? teams : [];
    const requestData = {
      capacityType,
      teamIds: teamsValue,
      startDate,
      endDate,
      assignmentTypes: [assignmentTypes],
      resourceCapacityPerDay: {
        field:
          fieldResourcesCapacityPerDay && fieldResourcesCapacityPerDay > 0
            ? fieldResourcesCapacityPerDay
            : 0,
        virtual:
          virtualResourcesCapacityPerDay && virtualResourcesCapacityPerDay > 0
            ? virtualResourcesCapacityPerDay
            : 0,
        claimOwner:
          claimOwnerResourcesCapacityPerDay &&
          claimOwnerResourcesCapacityPerDay > 0
            ? claimOwnerResourcesCapacityPerDay
            : 0,
      },
      resourceIds: assignedResourceIds.map((resource) => resource.value),
      ...(capacityType === 'ClaimOwner'
        ? {
            COLocationType,
            officeIds: offices || [],
            roles: [],
            resourceAttributes: transformedAttributes,
            perils: [],
            durationInMinutes: 0,
            driveTimeInMinutes: 0,
          }
        : {
            COLocationType: undefined,
            officeIds: [],
            roles,
            resourceAttributes: [],
            perils: [perils],
            durationInMinutes:
              durationInMinutes && durationInMinutes > 0
                ? durationInMinutes
                : 0,
            driveTimeInMinutes:
              averageDriveTime && averageDriveTime > 0 ? averageDriveTime : 0,
          }),
      locations,
    };

    return CapacityDashboard_RequestSchema.parse(requestData);
  };

  const onSubmit = (formData: CapacityFormData) => {
    try {
      let unassignedTasks = formData.unassignedTasks;
      if (formData.capacityType === 'ClaimOwner') {
        // For ClaimOwner, map COStates to unassignedTasks with count 0
        unassignedTasks = (formData.COStates || []).map((state) => ({
          location: state,
          count: 0,
        }));
      }

      const builtFilters = buildFilters({ ...formData, unassignedTasks });
      setFilters(builtFilters);
      mutate(builtFilters);
      // You can perform additional actions here, such as making an API call
    } catch (error) {
      if (error instanceof z.ZodError) {
        // eslint-disable-next-line no-console
        console.error('Validation error:', error.errors);
      } else {
        // eslint-disable-next-line no-console
        console.error('Unexpected error:', error);
      }
      setFilters(null);
    }
  };

  return (
    <>
      <CapacityDashboard
        loading={status === 'loading'}
        openState={{ open, setOpen }}
        data={data}
        unassignedTasks={[]}
        capacityType={filters?.capacityType || ''}
        filters={filters}
        companyTeamsLookup={companyTeamsLookup}
        perils={perils}
      />
      <CapacityDashboardFilters
        assignmentTypeOptions={assignmentTypes}
        companyTeams={companyTeams || []}
        onClose={() => setOpen(false)}
        isFilterDrawerOpen={open}
        onSubmit={onSubmit}
        company={company}
        perils={perilOptions}
        offices={offices}
      />
    </>
  );
};
